import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Spinner, Tabs, Tab, Form, Button, Table, Badge, Image, Modal as BootstrapModal, Alert } from 'react-bootstrap'; // Adicionado Badge, Image e BootstrapModal
import { Line, Bar } from 'react-chartjs-2'; // Adicionado Bar para possível gráfico
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement, // Adicionado para Bar chart
  ArcElement, // Adicionado para Pie/Doughnut chart
  Tooltip,
  Legend,
} from 'chart.js';
import axios from 'axios';
import { useEntidadeContext } from '../EntidadeContext'; // Importar contexto da entidade
import logoImage from '../img/logo.png'; // Importa a imagem da logo
import { useAuthContext } from '../context/AuthContext'; // Importar para verificar assinatura
import { useAlertModal } from '../AlertModalContext'; // Para feedback
import { useUserContext } from '../UserContext'; // Importar o contexto do usuário
import DashboardFinanceiroTab from '../components/financeiro/DashboardFinanceiroTab'; // Importar o componente da aba
import RelatoriosFaturamentoTab from '../components/financeiro/RelatoriosFaturamentoTab'; // Importar o componente da aba
import RelatoriosPagamentosTab from '../components/financeiro/RelatoriosPagamentosTab'; // Importar o componente da aba
import RelatoriosGerenciaisTab from '../components/financeiro/RelatoriosGerenciaisTab'; // Importar o componente da aba
import LancamentoDespesasTab from '../components/financeiro/LancamentoDespesasTab'; // Importar o componente da aba
import ConveniosTab from '../components/financeiro/ConveniosTab'; // Importar a nova aba de convênios


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Financeiro = () => {
  const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig, dynamicAccessConfig } = useUserContext(); // Obter estados do contexto
  const [financeiroResumo, setFinanceiroResumo] = useState(null);
  const { currentEntidade, isLoadingEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal();
  const { authState } = useAuthContext(); // Obter o estado de autenticação
  const navigate = useNavigate(); // Para o botão de redirecionamento

  // activeMainTabKey é usado para controlar qual aba principal está ativa.
  // O valor inicial pode ser a primeira aba permitida ou um fallback.
  const [activeMainTabKey, setActiveMainTabKey] = useState('');

  // Obter abas permitidas para Financeiro
  const permittedTabs = useMemo(() => {
    return getPermittedInternalTabs('financeiro', currentUserRole);
    },
    [getPermittedInternalTabs, currentUserRole, isLoadingAccessConfig, dynamicAccessConfig]
  );

  // NOVO: Memo para ordenar as abas, colocando 'resumoGeral' primeiro se existir.
  const sortedPermittedTabs = useMemo(() => {
    const resumoTab = permittedTabs.find(tab => tab.key === 'resumoGeral');
    if (resumoTab) {
      // Filtra as outras abas e coloca a de resumo no início
      const otherTabs = permittedTabs.filter(tab => tab.key !== 'resumoGeral');
      return [resumoTab, ...otherTabs];
    }
    // Se a aba de resumo não estiver presente, retorna a lista original
    return permittedTabs;
  }, [permittedTabs]);

  // Define a aba ativa inicial para a primeira permitida, ou 'resumoGeral' como fallback
  // Use useEffect para garantir que permittedTabs esteja pronto
  useEffect(() => {
    if (sortedPermittedTabs.length > 0 && !isLoadingAccessConfig) { // Garante que o loading terminou
      // Se activeMainTabKey não estiver definida ou não for uma aba permitida, define para a primeira permitida.
      if (!activeMainTabKey || !sortedPermittedTabs.some(tab => tab.key === activeMainTabKey)) {
        setActiveMainTabKey(sortedPermittedTabs[0].key);
      }
    }
  }, [sortedPermittedTabs, activeMainTabKey, isLoadingAccessConfig]); // Adiciona isLoadingAccessConfig


  // Estados para o modal de confirmação de exclusão de despesa
  const [showConfirmDeleteDespesaModal, setShowConfirmDeleteDespesaModal] = useState(false);
  const [despesaParaExcluir, setDespesaParaExcluir] = useState(null);

  // --- NOVOS ESTADOS PARA GESTÃO DE CONVÊNIOS ---
  const [showRecebidoModal, setShowRecebidoModal] = useState(false);
  const [showGlosaModal, setShowGlosaModal] = useState(false);
  const [pagamentoParaProcessar, setPagamentoParaProcessar] = useState(null);
  const [isProcessingConvenio, setIsProcessingConvenio] = useState(false);
  const [recebidoFormData, setRecebidoFormData] = useState({ valor_recebido: '', data_recebimento: new Date().toISOString().split('T')[0] });
  const [glosaJustificativa, setGlosaJustificativa] = useState('');
  // Chave para forçar o refresh da lista de pagamentos de convênio após uma ação
  const [refreshPagamentosKey, setRefreshPagamentosKey] = useState(0);

  // Função para abrir o modal de confirmação de exclusão (será passada para LancamentoDespesasTab)
  const handleOpenDeleteModal = (despesa) => {
    setDespesaParaExcluir(despesa);
    setShowConfirmDeleteDespesaModal(true);
  };

  const handleDeleteDespesa = async () => {
    if (!despesaParaExcluir) return;
    try {
      await axios.delete(`/api/despesas-operacionais/${despesaParaExcluir.id}`);
      showAlertModal('Sucesso', 'Despesa excluída com sucesso!', 'success');
      // A lista será recarregada dentro de LancamentoDespesasTab após a exclusão
    } catch (error) {
      showAlertModal('Erro', `Falha ao excluir despesa: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setShowConfirmDeleteDespesaModal(false);
      setDespesaParaExcluir(null);
    }
  };

  // --- NOVOS HANDLERS PARA GESTÃO DE CONVÊNIOS ---
  const handleOpenRecebidoModal = (pagamento) => {
    setPagamentoParaProcessar(pagamento);
    setRecebidoFormData({
      valor_recebido: pagamento.valor_a_receber || '',
      data_recebimento: new Date().toISOString().split('T')[0]
    });
    setShowRecebidoModal(true);
  };

  const handleOpenGlosaModal = (pagamento) => {
    setPagamentoParaProcessar(pagamento);
    setGlosaJustificativa('');
    setShowGlosaModal(true);
  };

  const handleCloseConvenioModals = () => {
    setShowRecebidoModal(false);
    setShowGlosaModal(false);
    setPagamentoParaProcessar(null);
  };

  const handleConfirmarRecebimento = async () => {
    if (!pagamentoParaProcessar) return;
    setIsProcessingConvenio(true);
    try {
      await axios.post(`/api/pagamentos/${pagamentoParaProcessar.pagamento_id}/marcar-recebido-convenio`, recebidoFormData);
      showAlertModal('Sucesso', 'Conta marcada como recebida!', 'success');
      setRefreshPagamentosKey(prev => prev + 1); // Força o refresh da lista
      handleCloseConvenioModals();
    } catch (error) {
      showAlertModal('Erro', `Falha ao marcar como recebido: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setIsProcessingConvenio(false);
    }
  };

  const handleConfirmarGlosa = async () => {
    if (!pagamentoParaProcessar) return;
    setIsProcessingConvenio(true);
    try {
      await axios.post(`/api/pagamentos/${pagamentoParaProcessar.pagamento_id}/marcar-glosado-convenio`, { justificativa_glosa: glosaJustificativa });
      showAlertModal('Sucesso', 'Conta marcada como glosada!', 'success');
      setRefreshPagamentosKey(prev => prev + 1); // Força o refresh da lista
      handleCloseConvenioModals();
    } catch (error) {
      showAlertModal('Erro', `Falha ao marcar como glosa: ${error.response?.data?.message || error.message}`, 'danger');
    } finally {
      setIsProcessingConvenio(false);
    }
  };


  const formatCurrency = (value) => {
    return value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
  };

  // --- INÍCIO DA VALIDAÇÃO DE ACESSO AO CONTEÚDO ---
  const isAccessBlocked = useMemo(() => {
    const subscription = authState.subscription;

    // Se a assinatura existe, não é vitalícia e não está ativa/em teste
    if (subscription && !subscription.plan?.is_lifetime) {
      const expirationDate = new Date(subscription.current_period_ends_at + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (today > expirationDate) {
        const diffTime = today.getTime() - expirationDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Bloqueia o acesso se expirou há mais de 5 dias
        if (diffDays > 5) {
          return true;
        }
      }
    }
    // Se nenhuma condição de bloqueio for atendida, permite o acesso.
    return false;
  }, [authState.subscription]);

  const BlockedContent = () => (
    <Alert variant="danger" className="text-center m-4">
      <Alert.Heading>Acesso ao Financeiro Restrito</Alert.Heading>
      <p>Seu plano expirou há mais de 5 dias. Para visualizar os dados financeiros e gerenciar suas contas, por favor, regularize sua assinatura.</p>
      <Button variant="danger" onClick={() => navigate('/pricing')}>Ver Planos e Regularizar</Button>
    </Alert>
  );

  if (isLoadingEntidade) { // Apenas o loading da entidade é relevante aqui agora
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </Container>
    );
  }

    if (isLoadingEntidade) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

    if (isLoadingEntidade) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .fin-sidebar-btn {
          width: 100%; display: flex; align-items: center;
          padding: 10px 14px; border: none; background: none;
          border-radius: 8px; cursor: pointer; text-align: left;
          font-family: 'Inter', 'Segoe UI', sans-serif;
          font-size: calc(13px * var(--font-scale)) font-weight: 500; color: #6b7280;
          transition: background 0.15s, color 0.15s;
          margin-bottom: 2px;
        }
        .fin-sidebar-btn:hover { background: #f1f5f9; color: #374151; }
        .fin-sidebar-btn.active { background: #eff6ff; color: #2563eb; font-weight: 600; }
        .fin-sidebar-btn.active .fin-sidebar-icon { color: #2563eb; }
        .fin-sidebar-icon { color: #9ca3af; flex-shrink: 0; transition: color 0.15s; margin-right: 10px; }

        .modal-header { padding: 0 !important; border: none !important; }
        .modal-header .btn-close { display: none !important; }
      `}</style>

      {/* ── CABEÇALHO ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
        padding: '20px 28px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <img src={logoImage} alt="Logo" style={{ height: '44px', width: 'auto', opacity: 0.9 }} />
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#fff' }}>
              Painel de Controle Financeiro
            </h2>
            <p style={{ margin: 0, fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,0.7)' }}>
              {currentEntidade?.nome || 'Clínica'}
            </p>
          </div>
        </div>
        <div style={{
          padding: '4px 12px', borderRadius: '999px',
          backgroundColor: 'rgba(255,255,255,0.15)',
          fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: '#fff',
          border: '1px solid rgba(255,255,255,0.25)',
        }}>
          {currentUserRole}
        </div>
      </div>

      {/* ── CONTEÚDO ── */}
      {isAccessBlocked ? (
        <div style={{ padding: '32px 28px' }}>
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: '12px', padding: '32px', textAlign: 'center',
          }}>
            <svg width="48" height="48" fill="none" stroke="#ef4444" viewBox="0 0 24 24"
              style={{ display: 'block', margin: '0 auto 16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h4 style={{ color: '#dc2626', fontWeight: '700', marginBottom: '8px' }}>Acesso ao Financeiro Restrito</h4>
            <p style={{ color: '#6b7280', fontSize: `calc(14px * var(--font-scale))`, marginBottom: '20px' }}>
              Seu plano expirou há mais de 5 dias. Para visualizar os dados financeiros, regularize sua assinatura.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                padding: '10px 24px', borderRadius: '8px', border: 'none',
                background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                color: '#fff', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600',
                cursor: 'pointer', boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
              }}
            >
              Ver Planos e Regularizar
            </button>
          </div>
        </div>
      ) : (
        /* ── LAYOUT: SIDEBAR + CONTEÚDO ── */
        <div style={{ display: 'flex', minHeight: 'calc(100vh - 84px)' }}>

          {/* ── SIDEBAR ── */}
          <aside style={{
            width: '220px', flexShrink: 0,
            backgroundColor: '#fff',
            borderRight: '1px solid #e5e7eb',
            padding: '16px 12px',
            display: 'flex', flexDirection: 'column',
          }}>
            <p style={{
              fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', color: '#9ca3af',
              textTransform: 'uppercase', letterSpacing: '0.8px',
              padding: '0 6px', marginBottom: '8px',
            }}>
              Seções
            </p>

            {sortedPermittedTabs.map(tabInfo => {
              /* Ícone por seção */
              const icons = {
                resumoGeral:           'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
                relatoriosFaturamento: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                relatoriosPagamentos:  'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
                relatoriosGerenciais:  'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                lancamentoDespesas:    'M12 6v6m0 0v6m0-6h6m-6 0H6',
                convenios:             'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
              };
              const iconPath = icons[tabInfo.key] || 'M4 6h16M4 12h16M4 18h16';

              return (
                <button
                  key={tabInfo.key}
                  className={`fin-sidebar-btn ${activeMainTabKey === tabInfo.key ? 'active' : ''}`}
                  onClick={() => setActiveMainTabKey(tabInfo.key)}
                >
                  <svg
                    className="fin-sidebar-icon"
                    width="16" height="16"
                    fill="none" stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                  </svg>
                  {tabInfo.title}
                </button>
              );
            })}
          </aside>

          {/* ── CONTEÚDO DA SEÇÃO ATIVA ── */}
          <main style={{ flex: 1, padding: '24px 28px', minWidth: 0 }}>

            {/* Breadcrumb / título da seção */}
            {(() => {
              const activeTab = sortedPermittedTabs.find(t => t.key === activeMainTabKey);
              if (!activeTab) return null;
              const icons = {
                resumoGeral:           'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
                relatoriosFaturamento: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                relatoriosPagamentos:  'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
                relatoriosGerenciais:  'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                lancamentoDespesas:    'M12 6v6m0 0v6m0-6h6m-6 0H6',
                convenios:             'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
              };
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '20px', paddingBottom: '16px',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="16" height="16" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d={icons[activeTab.key] || 'M4 6h16M4 12h16M4 18h16'} />
                    </svg>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                      {activeTab.title}
                    </h3>
                    <p style={{ margin: 0, fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                      Financeiro › {activeTab.title}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Componente da seção ativa */}
            {sortedPermittedTabs.map(tabInfo => {
              if (activeMainTabKey !== tabInfo.key) return null;

              if (tabInfo.key === 'resumoGeral') return (
                <DashboardFinanceiroTab
                  key={tabInfo.key} isActive
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                />
              );
              if (tabInfo.key === 'relatoriosFaturamento') return (
                <RelatoriosFaturamentoTab
                  key={tabInfo.key} isActive
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                />
              );
              if (tabInfo.key === 'relatoriosPagamentos') return (
                <RelatoriosPagamentosTab
                  key={tabInfo.key} isActive
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                  onMarcarRecebidoClick={handleOpenRecebidoModal}
                  onMarcarGlosadoClick={handleOpenGlosaModal}
                  refreshKey={refreshPagamentosKey}
                />
              );
              if (tabInfo.key === 'relatoriosGerenciais') return (
                <RelatoriosGerenciaisTab
                  key={tabInfo.key}
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                />
              );
              if (tabInfo.key === 'lancamentoDespesas') return (
                <LancamentoDespesasTab
                  key={tabInfo.key} isActive
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                  onDeleteClick={handleOpenDeleteModal}
                />
              );
              if (tabInfo.key === 'convenios') return (
                <ConveniosTab
                  key={tabInfo.key} isActive
                  currentEntidade={currentEntidade}
                  formatCurrency={formatCurrency}
                />
              );
              return null;
            })}
          </main>
        </div>
      )}

      {/* ══ MODAL: CONFIRMAR RECEBIMENTO ══ */}
      <BootstrapModal show={showRecebidoModal} onHide={handleCloseConvenioModals} centered>
        <BootstrapModal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{
            width: '100%', padding: '20px 24px',
            background: 'linear-gradient(135deg, #065f46, #059669)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div style={{ fontSize: `calc(15px * var(--font-scale))`, fontWeight: '700', color: '#fff', flex: 1 }}>Confirmar Recebimento do Convênio</div>
            <button onClick={handleCloseConvenioModals}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
            >×</button>
          </div>
        </BootstrapModal.Header>
        <BootstrapModal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#374151', marginBottom: '16px' }}>
            Confirmando recebimento para <strong>{pagamentoParaProcessar?.paciente_nome}</strong> no valor de <strong>{formatCurrency(pagamentoParaProcessar?.valor_a_receber)}</strong>.
          </p>
          {[
            { label: 'Valor Efetivamente Recebido (R$)*', type: 'number', key: 'valor_recebido', step: '0.01' },
            { label: 'Data do Recebimento*', type: 'date', key: 'data_recebimento' },
          ].map(field => (
            <div key={field.key} style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>{field.label}</label>
              <input type={field.type} step={field.step} value={recebidoFormData[field.key]}
                onChange={e => setRecebidoFormData({ ...recebidoFormData, [field.key]: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151', fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff' }}
                onFocus={e => { e.target.style.borderColor = '#059669'; e.target.style.boxShadow = '0 0 0 3px rgba(5,150,105,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          ))}
        </BootstrapModal.Body>
        <BootstrapModal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={handleCloseConvenioModals} disabled={isProcessingConvenio}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button onClick={handleConfirmarRecebimento} disabled={isProcessingConvenio}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isProcessingConvenio ? '#6ee7b7' : 'linear-gradient(135deg, #065f46, #059669)', color: '#fff', cursor: isProcessingConvenio ? 'not-allowed' : 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(5,150,105,0.3)' }}>
            {isProcessingConvenio && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
            {isProcessingConvenio ? 'Processando...' : 'Confirmar Recebimento'}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* ══ MODAL: GLOSA ══ */}
      <BootstrapModal show={showGlosaModal} onHide={handleCloseConvenioModals} centered>
        <BootstrapModal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{
            width: '100%', padding: '20px 24px',
            background: 'linear-gradient(135deg, #7f1d1d, #dc2626)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div style={{ fontSize: `calc(15px * var(--font-scale))`, fontWeight: '700', color: '#fff', flex: 1 }}>Marcar como Glosa</div>
            <button onClick={handleCloseConvenioModals}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
            >×</button>
          </div>
        </BootstrapModal.Header>
        <BootstrapModal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          <p style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#374151', marginBottom: '4px' }}>
            Marcando como <strong>Glosa</strong> a cobrança de <strong>{pagamentoParaProcessar?.paciente_nome}</strong> — <strong>{formatCurrency(pagamentoParaProcessar?.valor_a_receber)}</strong>.
          </p>
          <p style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af', marginBottom: '16px' }}>Esta ação indica que o convênio não efetuou o pagamento.</p>
          <div>
            <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '5px' }}>Justificativa (Opcional)</label>
            <textarea rows={3} value={glosaJustificativa}
              onChange={e => setGlosaJustificativa(e.target.value)}
              placeholder="Ex: Documentação incorreta, procedimento não coberto..."
              style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151', fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff', resize: 'vertical' }}
              onFocus={e => { e.target.style.borderColor = '#dc2626'; e.target.style.boxShadow = '0 0 0 3px rgba(220,38,38,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </BootstrapModal.Body>
        <BootstrapModal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={handleCloseConvenioModals} disabled={isProcessingConvenio}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button onClick={handleConfirmarGlosa} disabled={isProcessingConvenio}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isProcessingConvenio ? '#fca5a5' : 'linear-gradient(135deg, #7f1d1d, #dc2626)', color: '#fff', cursor: isProcessingConvenio ? 'not-allowed' : 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(220,38,38,0.3)' }}>
            {isProcessingConvenio && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
            {isProcessingConvenio ? 'Processando...' : 'Confirmar Glosa'}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* ══ MODAL: EXCLUIR DESPESA ══ */}
      <BootstrapModal show={showConfirmDeleteDespesaModal} onHide={() => setShowConfirmDeleteDespesaModal(false)} centered>
        <BootstrapModal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{
            width: '100%', padding: '20px 24px',
            background: 'linear-gradient(135deg, #7f1d1d, #dc2626)',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div style={{ fontSize: `calc(15px * var(--font-scale))`, fontWeight: '700', color: '#fff', flex: 1 }}>Confirmar Exclusão</div>
            <button onClick={() => setShowConfirmDeleteDespesaModal(false)}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', lineHeight: 1, flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
            >×</button>
          </div>
        </BootstrapModal.Header>
        <BootstrapModal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          <p style={{ fontSize: `calc(14px * var(--font-scale))`, color: '#374151' }}>
            Tem certeza que deseja excluir a despesa{' '}
            <strong>"{despesaParaExcluir?.descricao || despesaParaExcluir?.categoria}"</strong>{' '}
            no valor de <strong>{formatCurrency(despesaParaExcluir?.valor)}</strong>?
          </p>
          <p style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af', marginBottom: 0 }}>
            Esta ação não pode ser desfeita.
          </p>
        </BootstrapModal.Body>
        <BootstrapModal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={() => setShowConfirmDeleteDespesaModal(false)}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button onClick={handleDeleteDespesa}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #7f1d1d, #dc2626)', color: '#fff', cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(220,38,38,0.3)' }}>
            Excluir Despesa
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>

    </div>
  );

};

export default Financeiro;
